import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { PawPrint, CheckCircle2, Loader2, AlertTriangle, ArrowRight, Mail } from "lucide-react";
import { api } from "../api/client";
import { BASE_DOMAIN } from "../lib/subdomain";

// Pantalla a la que Stripe redirige justo después de que el visitante paga
// desde el modal "Ya quiero usarlo" del portal de inicio. El negocio TODAVÍA
// no existe en ese momento — se crea unos segundos después, cuando el
// webhook de Stripe confirma el pago (ver backend/stripeWebhook.routes.ts) —
// así que esta pantalla hace polling de /auth/alta/estado/:id hasta que ese
// trámite termine, y solo entonces muestra cómo entrar al negocio.
type Estado = "cargando" | "pendiente" | "completada" | "fallida" | "agotado";

const BTN_PRIMARIO = "inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-200 hover:bg-blue-700 hover:shadow-md";

const INTERVALO_MS = 2500;
const INTENTOS_MAXIMOS = 30; // ~75 segundos de espera antes de mostrar el mensaje de "tardó más de lo normal"

export default function AltaCompletado() {
  const [params] = useSearchParams();
  const solicitudId = params.get("solicitud");
  const [estado, setEstado] = useState<Estado>("cargando");
  const [subdominio, setSubdominio] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [mensajeError, setMensajeError] = useState<string | null>(null);
  const intentos = useRef(0);

  useEffect(() => {
    if (!solicitudId) {
      setEstado("fallida");
      setMensajeError("Falta información en el enlace. Si acabas de pagar, revisa tu correo en unos minutos.");
      return;
    }

    let activo = true;
    let temporizador: ReturnType<typeof setTimeout>;

    async function consultar() {
      try {
        const res = await api.get(`/auth/alta/estado/${solicitudId}`);
        if (!activo) return;
        const data = res.data;
        setEmail(data.email ?? null);

        if (data.estado === "COMPLETADA") {
          setSubdominio(data.subdominio);
          setEstado("completada");
          return;
        }
        if (data.estado === "FALLIDA") {
          setMensajeError(data.mensajeError ?? "No se pudo completar tu alta.");
          setEstado("fallida");
          return;
        }

        intentos.current += 1;
        if (intentos.current >= INTENTOS_MAXIMOS) {
          setEstado("agotado");
          return;
        }
        setEstado("pendiente");
        temporizador = setTimeout(consultar, INTERVALO_MS);
      } catch {
        if (!activo) return;
        intentos.current += 1;
        if (intentos.current >= INTENTOS_MAXIMOS) {
          setEstado("agotado");
          return;
        }
        temporizador = setTimeout(consultar, INTERVALO_MS);
      }
    }

    consultar();
    return () => {
      activo = false;
      clearTimeout(temporizador);
    };
  }, [solicitudId]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-white px-4 py-10">
      <div className="w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-xl">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600 text-white">
          <PawPrint size={24} />
        </div>

        {(estado === "cargando" || estado === "pendiente") && (
          <>
            <Loader2 size={36} className="mx-auto animate-spin text-blue-600" />
            <h1 className="mt-4 font-display text-xl text-slate-800">Confirmando tu pago...</h1>
            <p className="mt-2 text-sm text-slate-500">
              Esto toma solo unos segundos. No cierres ni recargues esta página.
            </p>
          </>
        )}

        {estado === "completada" && (
          <>
            <CheckCircle2 size={40} className="mx-auto text-blue-600" />
            <h1 className="mt-4 font-display text-xl text-slate-800">¡Tu negocio ya está listo!</h1>
            <p className="mt-2 text-sm text-slate-500">
              Tu pago se confirmó correctamente. Ya puedes entrar a tu panel de Mascotas.
            </p>
            <a
              href={`https://${subdominio}.${BASE_DOMAIN}/login${email ? `?correo=${encodeURIComponent(email)}` : ""}`}
              className={`${BTN_PRIMARIO} mt-6 w-full`}
            >
              Entrar a mi negocio <ArrowRight size={16} />
            </a>
            <div className="mt-5 rounded-xl bg-slate-50 p-4 text-left text-xs text-slate-500">
              <p className="font-semibold text-slate-600">Guarda este enlace:</p>
              <p className="mt-1 break-all">https://{subdominio}.{BASE_DOMAIN}/login</p>
              <p className="mt-2">Usa el correo con el que te registraste y la contraseña que elegiste.</p>
            </div>
            {email && (
              <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-slate-400">
                <Mail size={13} /> También te lo enviamos a {email}
              </p>
            )}
          </>
        )}

        {estado === "fallida" && (
          <>
            <AlertTriangle size={36} className="mx-auto text-amber-500" />
            <h1 className="mt-4 font-display text-xl text-slate-800">No pudimos completar tu alta</h1>
            <p className="mt-2 text-sm text-slate-500">{mensajeError}</p>
            <a href="mailto:soporte@mascotas.vet" className={`${BTN_PRIMARIO} mt-6 w-full`}>
              <Mail size={16} /> Contactar a soporte
            </a>
          </>
        )}

        {estado === "agotado" && (
          <>
            <AlertTriangle size={36} className="mx-auto text-amber-500" />
            <h1 className="mt-4 font-display text-xl text-slate-800">Tu pago sigue procesándose</h1>
            <p className="mt-2 text-sm text-slate-500">
              Está tardando más de lo normal. En cuanto se confirme, te llegará un correo con el acceso a tu negocio.
              Si en unos minutos no te llega nada, escríbenos.
            </p>
            <a href="mailto:soporte@mascotas.vet" className={`${BTN_PRIMARIO} mt-6 w-full`}>
              <Mail size={16} /> Contactar a soporte
            </a>
          </>
        )}

        <p className="mt-6 text-center text-sm text-slate-400">
          <Link to="/" className="font-medium text-blue-600 hover:underline">
            Volver al inicio
          </Link>
        </p>
      </div>
    </div>
  );
}
